import React, { useEffect, useRef, useState } from 'react';
import { experience } from '../data/mockData';
import { Briefcase, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';

const ExperienceCard = ({ exp, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 delay-${index * 100} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="relative pl-8 pb-12 last:pb-0">
        {/* Timeline line */}
        {index < experience.length - 1 && (
          <div className="absolute left-[11px] top-10 bottom-0 w-px bg-gray-200 dark:bg-white/10"></div>
        )}

        {/* Timeline dot */}
        <div className="absolute left-0 top-1 w-[23px] h-[23px] rounded-full border-2 border-emerald-500 bg-white dark:bg-[#09090B] flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
        </div>

        {/* Content */}
        <div className="group p-6 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 hover:border-emerald-200 dark:hover:border-emerald-500/20 transition-colors duration-300">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {exp.role}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Briefcase className="h-3.5 w-3.5 text-emerald-500" />
                <span>{exp.company}</span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span>{exp.location}</span>
              </div>
            </div>
            <span className="text-sm font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 px-3 py-1 rounded-md">
              {exp.duration}
            </span>
          </div>

          <ul className="space-y-3 mb-4">
            {exp.description.map((item, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                <ChevronRight className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2">
            {exp.tech.map((t) => (
              <Badge
                key={t}
                variant="secondary"
                className="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0 text-xs font-normal"
              >
                {t}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExperienceSection = () => {
  return (
    <section id="experience" className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-12">
          <div className="h-px w-12 bg-emerald-500"></div>
          <h2 className="font-mono text-sm uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            Experience
          </h2>
        </div>

        <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-12">
          Where I've Worked
        </h3>

        {/* Timeline */}
        <div className="max-w-3xl">
          {experience.map((exp, index) => (
            <ExperienceCard key={exp.id} exp={exp} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;